import { useMemo, useState } from "react";
import { ForBorrowerAge } from "../../../../features/gms-proposal-forms/age-bracket-and-benefit-amount/age-bracket-and-benefit-amount.state";
import { formatNumberWithComma } from "../../../../utils/format-numbers.utils";
import { THead } from "../../../../features/auth/auth-state";
import { visibleRemainingAge } from "../../../../features/gms-proposal-forms/age-bracket-and-benefit-amount/age-bracket-and-benefit-amount.slice";
import { useSelector } from "react-redux";
import Input from "../../../ui/input";
import { defaultStyle } from "../../../../constant/custom-style";
import SwitchYesNo from "../../../ui/switch-yes-no";
import { proposedVisibleRemainingAge } from "../../../../features/propose-data/propose-data-slice";

interface BorrowerAgeItem extends ForBorrowerAge {
    typeOfAge?: string;
}

interface BorrowerConfiguration {
    borrowerAgeOptionsList?: ForBorrowerAge[];
    remainingAgeList?: BorrowerAgeItem[];
    isProductGPA: boolean;
    isProductGCLI: boolean;
    isProductGYRT: boolean;
    isProductGCI: boolean;
}

interface Props {
    isShow: boolean;
    current: BorrowerConfiguration;
    proposed: BorrowerConfiguration;
}

interface ComparableBorrower {
    id: number;
    label: string;
    value: boolean;
    amount: number;
    typeOfAge?: string;
}

type BorrowerChangeType = "added" | "removed" | "changed";

interface BorrowerComparison {
    id: number;
    current?: ComparableBorrower;
    proposed?: ComparableBorrower;
    type: BorrowerChangeType;
}

const normalizeString = (value: unknown): string => {
    if (value === null || value === undefined) return "";
    return String(value).trim();
};

const normalizeAmount = (value: unknown): number => {
    if (value === null || value === undefined || value === "") return 0;

    const parsed = Number(
        String(value).replace(/,/g, "").trim(),
    );

    return Number.isNaN(parsed) ? 0 : parsed;
};

const normalizeBoolean = (value: unknown): boolean => {
    return Boolean(value);
};

const buildBorrowerRows = (configuration: BorrowerConfiguration): ComparableBorrower[] => {
    const rowMap = new Map<number, ComparableBorrower>();

    const regularRows = (
        configuration.borrowerAgeOptionsList ?? []
    ).filter((item) => {
        if (!item.isShow) return false;

        if (
            configuration.isProductGCI &&
            item.id === 6
        ) {
            return false;
        }

        return true;
    });

    regularRows.forEach((item) => {
        rowMap.set(Number(item.id), {
            id: Number(item.id),
            label: normalizeString(item.label),
            value: normalizeBoolean(item.value),
            amount: normalizeAmount(item.amount),
            typeOfAge: undefined,
        });
    });

    const remainingRows = (
        configuration.remainingAgeList ?? []
    ).filter(
        (item) =>
            normalizeString(item.typeOfAge) !==
            "under_min",
    );

    remainingRows.forEach((item) => {
        const id = Number(item.id);

        rowMap.set(id, {
            id,
            label: normalizeString(item.label),
            value: normalizeBoolean(item.value),
            amount: normalizeAmount(item.amount),
            typeOfAge: item.typeOfAge,
        });
    });

    return Array.from(rowMap.values()).sort(
        (a, b) => a.id - b.id,
    );
};

const areBorrowerValuesEqual = (current?: ComparableBorrower, proposed?: ComparableBorrower): boolean => {
    if (!current || !proposed) return false;

    return (
        current.id === proposed.id &&
        current.value === proposed.value &&
        current.amount === proposed.amount
    );
};

const getBorrowerChanges = (current: ComparableBorrower[], proposed: ComparableBorrower[]): BorrowerComparison[] => {
    const currentMap = new Map<number, ComparableBorrower>();
    const proposedMap = new Map<number, ComparableBorrower>();

    current.forEach((item) => {
        currentMap.set(item.id, item);
    });

    proposed.forEach((item) => {
        proposedMap.set(item.id, item);
    });

    const ids: number[] = [];

    currentMap.forEach((_, id) => {
        if (ids.indexOf(id) === -1) {
            ids.push(id);
        }
    });

    proposedMap.forEach((_, id) => {
        if (ids.indexOf(id) === -1) {
            ids.push(id);
        }
    });

    const changes: BorrowerComparison[] = [];

    ids.forEach((id) => {
        const currentItem = currentMap.get(id);
        const proposedItem = proposedMap.get(id);

        if (!currentItem && proposedItem) {
            changes.push({
                id,
                proposed: proposedItem,
                type: "added",
            });

            return;
        }

        if (currentItem && !proposedItem) {
            changes.push({
                id,
                current: currentItem,
                type: "removed",
            });

            return;
        }

        if (
            currentItem &&
            proposedItem &&
            !areBorrowerValuesEqual(
                currentItem,
                proposedItem,
            )
        ) {
            changes.push({
                id,
                current: currentItem,
                proposed: proposedItem,
                type: "changed",
            });
        }
    });

    return changes.sort((a, b) => a.id - b.id);
};

const NoChanges = () => {
    return (
        <div className="flex min-h-[72px] items-center gap-2 rounded-md bg-gray-50 px-3 py-3 text-[11px] text-gray-400">
            <svg viewBox="0 0 24 24" fill="none" className="h-4 w-4 shrink-0" aria-hidden="true" >
                <path d="m5 12.5 4.5 4L19 7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>No changes</span>
        </div>
    );
};

export default function ProposedAmendBorrowers({isShow, current, proposed,}: Props) {
    
    const [showContent, setShowContent] = useState(true);

    const currentRows = useMemo(
        () => buildBorrowerRows(current),
        [current],
    );

    const proposedRows = useMemo(
        () => buildBorrowerRows(proposed),
        [proposed],
    );

    const changes = useMemo(
        () => getBorrowerChanges(
            currentRows,
            proposedRows,
        ),
        [currentRows, proposedRows],
    );

    const currentShowRate = current.isProductGCLI || current.isProductGYRT || current.isProductGCI
    const currentAgeBracket = useSelector(visibleRemainingAge).filter(item => item.typeOfAge !== "under_min");
    const currentAgeBracketList = current.borrowerAgeOptionsList?.filter(item => item.isShow && !(current.isProductGCI && item.id === 6));
    const currentAgeRemainingActive = currentAgeBracket.filter(item => item.value)
    const currentAgeBracketActive = currentAgeBracketList?.filter(item => item.value)

    const currentTheadForSummaryOfBenefits = useMemo<THead[]>(() => [
        { id: 1, name: "Eligibility Age" },
        { id: 2, name: "Benefit Amount" },
        { id: 3, name: "Rate" },
    ], [current.isProductGPA]);


    const proposedShowRate = proposed.isProductGCLI || proposed.isProductGYRT || proposed.isProductGCI
    const proposedAgeBracket = useSelector(proposedVisibleRemainingAge).filter(item => item.typeOfAge !== "under_min");
    const proposedAgeBracketList = proposed.borrowerAgeOptionsList?.filter(item => item.isShow && !(proposed.isProductGCI && item.id === 6));
    const proposedAgeRemainingActive = proposedAgeBracket.filter(item => item.value)
    const proposedAgeBracketActive = proposedAgeBracketList?.filter(item => item.value)
    
    const proposedTheadForSummaryOfBenefits = useMemo<THead[]>(() => [
        { id: 1, name: "Eligibility Age" },
        { id: 2, name: "Benefit Amount" },
        { id: 3, name: "Rate" },
    ], [proposed.isProductGPA]);

    if (!isShow || changes.length === 0) {
        return null;
    }

    const showRate =
        current.isProductGCLI ||
        current.isProductGYRT ||
        current.isProductGCI ||
        proposed.isProductGCLI ||
        proposed.isProductGYRT ||
        proposed.isProductGCI;

    return (
        <div className="grid grid-cols-1 gap-3.5">
            <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                {/* MAIN HEADER */}
                <button
                    type="button"
                    onClick={() => setShowContent((previous) =>!previous,)}
                    className="flex w-full items-center justify-between gap-3 border-b border-slate-200 bg-white px-4 py-2.5 text-left transition-colors hover:bg-slate-50"
                >
                    <div className="flex min-w-0 items-center gap-2">
                        <svg viewBox="0 0 24 24" fill="none" className={`h-3.5 w-3.5 shrink-0 text-[#186cb2] transition-transform ${showContent ? "rotate-0" : "-rotate-90"}`} aria-hidden="true" >
                            <path d="m7 10 5 5 5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <h2 className="text-[11px] font-semibold uppercase tracking-wide text-slate-700">
                            Eligibility Age and Benefit Amount
                        </h2>
                        <span className="rounded-full bg-[#e3f1fa] px-1.5 py-0.5 text-[8px] font-bold text-[#186cb2] ring-1 ring-[#c7e0f3]">
                            {changes.length}
                        </span>
                    </div>
                    <span className="shrink-0 text-[9px] font-medium text-slate-400">
                        {showContent ? "Hide" : "Show"}
                    </span>
                </button>
                {showContent && (
                    <>
                        {/* CURRENT / PROPOSED HEADER */}
                        <div className="grid grid-cols-1 border-b border-slate-200 sm:grid-cols-2">
                            <div className="border-b border-slate-200 bg-slate-50 px-4 py-2.5 sm:border-b-0 sm:border-r">
                                <h3 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                    Current
                                </h3>
                            </div>
                            <div className="bg-[#f5f9fc] px-4 py-2.5">
                                <h3 className="text-[11px] font-semibold uppercase tracking-wide text-[#186cb2]">
                                    Proposed
                                </h3>
                            </div>
                        </div>
                        {/* CONTENT */}
                        <div className="grid grid-cols-1 sm:grid-cols-2">
                            <div className="min-w-0 border-b border-slate-200 p-3 sm:border-b-0 sm:border-r sm:p-4">
                                {changes.length === 0 ? (
                                    <NoChanges />
                                ) : (
                                    <div className="w-full overflow-hidden rounded-lg border border-gray-200">
                                        <div className="space-y-3">
                                            <table className="w-full table-fixed border-collapse">
                                                <thead>
                                                    <tr className="bg-gray-50 text-gray-700">
                                                        {currentTheadForSummaryOfBenefits.map((head) => (
                                                            <th key={head.id} className={`${head.id === 1 ? 'w-[30%]' : head.id === 2 ? 'w-[50%]' : 'w-[20%]'} border-b border-gray-200 px-3 py-2.5 text-left text-xs font-semibold`}>
                                                                {head.name}
                                                            </th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {currentAgeRemainingActive.map((rem) => {
                                                        const fAmount = formatNumberWithComma(rem.amount ?? '')
                                                        const formatFAmount = fAmount.length === 0 ? '0' : fAmount
                                                        const isRates = rem.value ? 'Yes' : 'No'
                                                        return (
                                                            <tr key={rem.id} className="transition-colors hover:bg-gray-50">
                                                                <td className="border-b border-r border-gray-100 px-3 py-2.5">
                                                                    <div className="flex items-center gap-2">
                                                                        <div className={`h-2 w-2 shrink-0 rounded-full ${rem.value ? "bg-[#186cb2]" : "bg-gray-300"}`} />
                                                                        <span className="text-xs font-medium text-gray-800">
                                                                            {rem.label}
                                                                        </span>
                                                                    </div>
                                                                </td>
                                                                <td className="border-b border-gray-100 px-2 py-1.5">
                                                                    <span className="text-xs font-medium text-gray-800">{formatFAmount}</span>
                                                                </td>
                                                                <td className="border-b border-l border-gray-100 px-3 py-1.5 text-center">
                                                                        {/* <span className="text-xs font-medium text-[#186cb2]">{isRates}</span> */}
                                                                        <SwitchYesNo
                                                                            isPreview={true} 
                                                                            value={rem.value} 
                                                                            id={rem.id}
                                                                            onChange={() => {}} 
                                                                            type="rAge"
                                                                            label={rem.label} 
                                                                        />
                                                                    </td>
                                                            </tr>
                                                        )
                                                    })}
                                                    {currentShowRate && [...(currentAgeBracketActive ?? [])].sort((a, b) => a.id - b.id).map((body) => {
                                                        const fAmount = formatNumberWithComma(body.amount ?? '')
                                                        const formatFAmount = fAmount.length === 0 ? '0' : fAmount
                                                        const isRates = body.value ? 'Yes' : 'No'
                                                        return (
                                                            <tr key={body.id} className="transition-colors hover:bg-gray-50">
                                                                <td className="border-b border-r border-gray-100 px-3 py-2.5">
                                                                    <div className="flex items-center gap-2">
                                                                        <div className={`h-2 w-2 shrink-0 rounded-full ${body.value ? "bg-[#186cb2]" : "bg-gray-300"}`} />
                                                                        <span className="text-xs font-medium text-gray-800">
                                                                            {body.label}
                                                                        </span>
                                                                    </div>
                                                                </td>
                                                                <td className="border-b border-gray-100 px-2 py-1.5">
                                                                    <span className="text-xs font-medium text-gray-800">{formatFAmount}</span>
                                                                </td>
                                                                {!current.isProductGPA && (
                                                                    <td className="border-b border-l border-gray-100 px-3 py-1.5 text-center">
                                                                        <SwitchYesNo 
                                                                            isPreview={true} 
                                                                            value={body.value} 
                                                                            id={body.id} 
                                                                            onChange={() => {}} 
                                                                            type="fAge" 
                                                                            label={body.label} 
                                                                        />
                                                                    </td>
                                                                )}
                                                            </tr>
                                                        )
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="min-w-0 p-3 sm:p-4">
                                {changes.length === 0 ? (
                                    <NoChanges />
                                ) : (
                                    <div className="w-full overflow-hidden rounded-lg border border-gray-200">
                                        <div className="space-y-3">
                                            <table className="w-full table-fixed border-collapse">
                                                <thead>
                                                    <tr className="bg-gray-50 text-gray-700">
                                                        {proposedTheadForSummaryOfBenefits.map((head) => (
                                                            <th key={head.id} className={`${head.id === 1 ? 'w-[30%]' : head.id === 2 ? 'w-[50%]' : 'w-[20%]'} border-b border-gray-200 px-3 py-2.5 text-left text-xs font-semibold`}>
                                                                {head.name}
                                                            </th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {proposedAgeRemainingActive.map((rem) => {
                                                        const fAmount = formatNumberWithComma(rem.amount ?? '')
                                                        const formatFAmount = fAmount.length === 0 ? '0' : fAmount
                                                        const isRates = rem.value ? 'Yes' : 'No'

                                                        return (
                                                            <tr key={rem.id} className="transition-colors hover:bg-gray-50">
                                                                <td className="border-b border-r border-gray-100 px-3 py-2.5">
                                                                    <div className="flex items-center gap-2">
                                                                        <div className={`h-2 w-2 shrink-0 rounded-full ${rem.value ? "bg-[#186cb2]" : "bg-gray-300"}`} />
                                                                        <span className="text-xs font-medium text-gray-800">
                                                                            {rem.label}
                                                                        </span>
                                                                    </div>
                                                                </td>
                                                                <td className="border-b border-gray-100 px-2 py-1.5">
                                                                    <span className="text-xs font-medium text-gray-800">{formatFAmount}</span>
                                                                </td>
                                                                <td className="border-b border-l border-gray-100 px-3 py-1.5 text-center">
                                                                        <SwitchYesNo
                                                                            isPreview={true} 
                                                                            value={rem.value} 
                                                                            id={rem.id} 
                                                                            onChange={() => {}} 
                                                                            type="rAge" 
                                                                            label={rem.label} 
                                                                        />
                                                                    </td>
                                                            </tr>
                                                        )
                                                    })}
                                                    {proposedShowRate && [...(proposedAgeBracketActive ?? [])].sort((a, b) => a.id - b.id).map((body) => {
                                                        const fAmount = formatNumberWithComma(body.amount ?? '')
                                                        const formatFAmount = fAmount.length === 0 ? '0' : fAmount
                                                        const isRates = body.value ? 'Yes' : 'No'

                                                        return (
                                                            <tr key={body.id} className="transition-colors hover:bg-gray-50">
                                                                <td className="border-b border-r border-gray-100 px-3 py-2.5">
                                                                    <div className="flex items-center gap-2">
                                                                        <div className={`h-2 w-2 shrink-0 rounded-full ${body.value ? "bg-[#186cb2]" : "bg-gray-300"}`} />
                                                                        <span className="text-xs font-medium text-gray-800">
                                                                            {body.label}
                                                                        </span>
                                                                    </div>
                                                                </td>
                                                                <td className="border-b border-gray-100 px-2 py-1.5">
                                                                    <span className="text-xs font-medium text-gray-800">{formatFAmount}</span>
                                                                </td>
                                                                {!proposed.isProductGPA && (
                                                                    <td className="border-b border-l border-gray-100 px-3 py-1.5 text-center">
                                                                        <SwitchYesNo 
                                                                            isPreview={true} 
                                                                            value={body.value} 
                                                                            id={body.id} 
                                                                            onChange={() => {}} 
                                                                            type="fAge" 
                                                                            label={body.label} 
                                                                        />
                                                                    </td>
                                                                )}
                                                            </tr>
                                                        )
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}